/**
 * 品項分配區塊元件
 * 顯示每個訂單明細的分配狀態，提供分配/取消操作
 * 
 * 命名規範：
 * - Product（產品）：器材型號
 * - Item（品項）：實體設備，有唯一序號
 */

import { useState, useEffect } from 'react';
import type { OrderItemData } from '../../../../../modules/order';
import type {
  AvailableItem,
  ItemConflict,
  AllocationRequest,
  AllocationType,
} from '../../../../../modules/allocation';
import {
  fetchAvailableItems,
  allocateFromInventory,
  allocateExternal,
  cancelAllocation,
  getCurrentAdmin,
} from '../../../../../modules/allocation';
import {
  formatProductName,
  formatSerialNumber,
} from '../../../../../modules/product';

interface Props {
  orderId: string;
  orderItems: OrderItemData[];
  startDate: string;
  endDate: string;
  onAllocationChange: () => void;
}

export default function AllocationSection({
  orderId,
  orderItems,
  startDate,
  endDate,
  onAllocationChange,
}: Props) {
  const allItemsAllocated = orderItems.every(
    (item) => item.equipment_item_id || item.is_external_dispatch,
  );

  // 彈窗狀態
  const [showModal, setShowModal] = useState(false);
  const [currentReq, setCurrentReq] = useState<AllocationRequest | null>(null);
  const [allocationType, setAllocationType] = useState<AllocationType>('inventory');
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [conflicts, setConflicts] = useState<ItemConflict[]>([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [externalNote, setExternalNote] = useState('');
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // 自動隱藏 toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  /** 取得產品顯示名稱 */
  const getProductDisplayName = (item: OrderItemData): string => {
    if (item.product) {
      return formatProductName(item.product, true);
    }
    return '未知產品';
  };

  const handleOpenAllocation = async (item: OrderItemData) => {
    const productName = getProductDisplayName(item);
    const req: AllocationRequest = {
      orderItemId: item.id,
      orderId,
      productId: item.product_id || '',
      productName,
      quantity: item.quantity,
      startDate,
      endDate,
    };
    setCurrentReq(req);
    setAllocationType('inventory');
    setSelectedItemId('');
    setExternalNote('');
    setShowModal(true);

    // 查詢可用品項
    setLoadingItems(true);
    try {
      const result = await fetchAvailableItems(
        req.productId,
        startDate,
        endDate,
        orderId,
      );
      setAvailableItems(result.items);
      setConflicts(result.conflicts);

      // 如果沒有可用庫存，自動切到同行調度
      if (result.items.length === 0) {
        setAllocationType('external');
      }
    } catch (err) {
      console.error('fetchAvailableItems error:', err);
      setAvailableItems([]);
      setConflicts([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSave = async () => {
    if (!currentReq) return;
    setSaving(true);

    const admin = await getCurrentAdmin();
    if (!admin) {
      setToast({ type: 'error', msg: '無法取得管理員身份，請重新登入' });
      setSaving(false);
      return;
    }

    if (allocationType === 'inventory') {
      if (!selectedItemId) {
        setToast({ type: 'error', msg: '請選擇品項序號' });
        setSaving(false);
        return;
      }
      const selectedItem = availableItems.find((i) => i.id === selectedItemId);
      const serialDisplay = selectedItem ? formatSerialNumber(selectedItem.serial_number) : selectedItemId;
      
      const result = await allocateFromInventory(
        currentReq.orderItemId,
        selectedItemId,
        {
          orderId: currentReq.orderId,
          actionType: 'allocation_inventory',
          description: `分配庫存品項「${currentReq.productName}」序號 ${serialDisplay}`,
          newValue: serialDisplay,
          adminId: admin.id,
          adminName: admin.name,
        },
      );

      if (result.success) {
        setToast({ type: 'success', msg: result.message });
        setShowModal(false);
        onAllocationChange();
      } else {
        setToast({ type: 'error', msg: result.message });
      }
    } else {
      if (!externalNote.trim()) {
        setToast({ type: 'error', msg: '請填寫調度來源' });
        setSaving(false);
        return;
      }
      const result = await allocateExternal(
        currentReq.orderItemId,
        externalNote,
        {
          orderId: currentReq.orderId,
          actionType: 'allocation_external',
          description: `同行調度「${currentReq.productName}」- ${externalNote}`,
          newValue: externalNote,
          adminId: admin.id,
          adminName: admin.name,
        },
      );

      if (result.success) {
        setToast({ type: 'success', msg: result.message });
        setShowModal(false);
        onAllocationChange();
      } else {
        setToast({ type: 'error', msg: result.message });
      }
    }
    setSaving(false);
  };

  const handleCancel = async (item: OrderItemData) => {
    const admin = await getCurrentAdmin();
    if (!admin) {
      setToast({ type: 'error', msg: '無法取得管理員身份' });
      return;
    }

    const productName = getProductDisplayName(item);
    const label = item.equipment_item_id
      ? `庫存品項 ${item.equipment_item?.serial_number ? formatSerialNumber(item.equipment_item.serial_number) : ''}`
      : `同行調度（${item.external_dispatch_note || ''})`;

    const result = await cancelAllocation(
      item.id,
      item.equipment_item_id || null,
      {
        orderId,
        actionType: 'allocation_cancel',
        description: `取消分配「${productName}」- ${label}`,
        oldValue: label,
        adminId: admin.id,
        adminName: admin.name,
      },
    );

    if (result.success) {
      setToast({ type: 'success', msg: result.message });
      onAllocationChange();
    } else {
      setToast({ type: 'error', msg: result.message });
    }
  };

  return (
    <>
      {/* Toast 通知 */}
      {toast && (
        <div className="fixed top-6 right-6 z-[100] animate-fade-in">
          <div
            className={`px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium flex items-center gap-2 ${
              toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
            }`}
          >
            <i className={toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}></i>
            {toast.msg}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-box-3-line text-teal-600"></i>
            品項分配
          </h2>
          {allItemsAllocated ? (
            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold">
              <i className="ri-checkbox-circle-line mr-1"></i>
              已完成
            </span>
          ) : (
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
              <i className="ri-time-line mr-1"></i>
              待分配 ({orderItems.filter((i) => !i.equipment_item_id && !i.is_external_dispatch).length}/{orderItems.length})
            </span>
          )}
        </div>

        <div className="text-xs text-gray-500 mb-4 flex items-center gap-1">
          <i className="ri-calendar-line"></i>
          租借期間：{new Date(startDate).toLocaleDateString('zh-TW')} ~ {new Date(endDate).toLocaleDateString('zh-TW')}
        </div>

        <div className="space-y-3">
          {orderItems.map((item) => {
            const productName = getProductDisplayName(item);
            return (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1">
                      {productName}
                    </div>
                    {item.product?.code && (
                      <div className="text-xs text-gray-500 font-mono mb-1">
                        產品代碼：{item.product.code}
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      需求數量：{item.quantity}
                    </div>
                  </div>
                  {!item.equipment_item_id && !item.is_external_dispatch ? (
                    <button
                      onClick={() => handleOpenAllocation(item)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line mr-1"></i>
                      分配品項
                    </button>
                  ) : (
                    <button
                      onClick={() => handleCancel(item)}
                      className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-line mr-1"></i>
                      取消分配
                    </button>
                  )}
                </div>

                {/* 已分配 - 庫存 */}
                {item.equipment_item_id && (
                  (() => {
                    // ✅ 修正：使用 is_urent_item 欄位判斷，而非序號前綴
                    const isUrentItem = !!item.is_urent_item;
                    return isUrentItem ? (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-purple-800 mb-2">
                          <i className="ri-checkbox-circle-fill"></i>
                          <span className="font-medium">已分配（Urent 器材）</span>
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            Urent
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div>
                            品項序號：
                            <span className="font-mono font-medium text-purple-900">
                              {item.equipment_item?.serial_number 
                                ? formatSerialNumber(item.equipment_item.serial_number) 
                                : '-'}
                            </span>
                          </div>
                          <div>位置：{item.equipment_item?.location_name_zh || '未設定'}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-emerald-800 mb-2">
                          <i className="ri-checkbox-circle-fill"></i>
                          <span className="font-medium">已分配（公司庫存）</span>
                        </div>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div>
                            品項序號：
                            <span className="font-mono font-medium">
                              {item.equipment_item?.serial_number 
                                ? formatSerialNumber(item.equipment_item.serial_number) 
                                : '-'}
                            </span>
                          </div>
                          <div>位置：{item.equipment_item?.location_name_zh || '未設定'}</div>
                        </div>
                      </div>
                    );
                  })()
                )}

                {/* 已分配 - 同行調度 */}
                {item.is_external_dispatch && (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-sky-800 mb-2">
                      <i className="ri-truck-line"></i>
                      <span className="font-medium">已分配（同行調度）</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      調度來源：{item.external_dispatch_note}
                    </div>
                  </div>
                )}

                {/* 未分配 */}
                {!item.equipment_item_id && !item.is_external_dispatch && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-amber-800">
                      <i className="ri-alert-line"></i>
                      <span className="font-medium">未分配</span>
                    </div>
                  </div>
                )}

                {/* 配件清單提示 */}
                {item.product?.accessories && item.product.accessories.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <i className="ri-checkbox-multiple-line"></i>
                      出貨配件清點：
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {item.product.accessories.map((acc, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          {acc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 分配彈窗 */}
      {showModal && currentReq && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">分配品項</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentReq.productName} × {currentReq.quantity}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* 分配方式 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">分配方式</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setAllocationType('inventory')}
                    className={`px-4 py-3 rounded-lg border-2 transition-all cursor-pointer text-center ${
                      allocationType === 'inventory'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-box-3-line text-xl"></i>
                    </div>
                    <div className="font-medium text-sm">公司庫存</div>
                    <div className="text-xs mt-1 opacity-70">
                      {loadingItems ? '查詢中...' : `${availableItems.length} 台可用`}
                    </div>
                  </button>
                  <button
                    onClick={() => setAllocationType('external')}
                    className={`px-4 py-3 rounded-lg border-2 transition-all cursor-pointer text-center ${
                      allocationType === 'external'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-truck-line text-xl"></i>
                    </div>
                    <div className="font-medium text-sm">同行調度</div>
                    <div className="text-xs mt-1 opacity-70">向合作夥伴調度</div>
                  </button>
                </div>
              </div>

              {/* 庫存選擇 */}
              {allocationType === 'inventory' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選擇品項序號
                  </label>
                  {loadingItems ? (
                    <div className="text-center py-8 text-gray-500">
                      <i className="ri-loader-4-line text-2xl animate-spin"></i>
                      <p className="mt-2 text-sm">查詢可用品項中...</p>
                    </div>
                  ) : availableItems.length > 0 ? (
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {availableItems.map((availItem) => {
                        // ✅ 修正：使用 is_urent_equipment 欄位判斷，而非序號前綴
                        const isUrent = !!(availItem as any).is_urent_equipment;
                        
                        return (
                          <button
                            key={availItem.id}
                            onClick={() => setSelectedItemId(availItem.id)}
                            className={`w-full px-4 py-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                              selectedItemId === availItem.id
                                ? isUrent
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-teal-500 bg-teal-50'
                                : isUrent
                                ? 'border-purple-200 bg-purple-50/30 hover:border-purple-300'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className={`font-mono font-medium ${isUrent ? 'text-purple-900' : 'text-gray-900'}`}>
                                    {formatSerialNumber(availItem.serial_number)}
                                  </div>
                                  {isUrent && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                                      Urent
                                    </span>
                                  )}
                                </div>
                                <div className={`text-xs mt-1 ${isUrent ? 'text-purple-600' : 'text-gray-500'}`}>
                                  <i className="ri-map-pin-line mr-1"></i>
                                  {availItem.location_name_zh}
                                </div>
                              </div>
                              {selectedItemId === availItem.id && (
                                <i className={`ri-checkbox-circle-fill text-xl ${isUrent ? 'text-purple-600' : 'text-teal-600'}`}></i>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-inbox-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-500 text-sm">目前沒有可用的庫存品項</p>
                      <p className="text-gray-400 text-xs mt-1">請使用同行調度方式</p>
                    </div>
                  )}

                  {/* 衝突提示 */}
                  {conflicts.length > 0 && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-amber-800 mb-2 text-sm font-medium">
                        <i className="ri-error-warning-line"></i>
                        以下品項在此期間已被佔用
                      </div>
                      <div className="space-y-1">
                        {conflicts.map((c, idx) => (
                          <div key={idx} className="text-xs text-amber-700">
                            訂單 {c.order_number}（{new Date(c.start_date).toLocaleDateString('zh-TW')} ~ {new Date(c.end_date).toLocaleDateString('zh-TW')}）
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 同行調度 */}
              {allocationType === 'external' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    調度來源 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={externalNote}
                    onChange={(e) => setExternalNote(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例如：向 XX 影視器材公司調度，聯絡人：王先生 0912-345-678"
                  />
                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {externalNote.length}/500
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1"></i>
                    處理中...
                  </>
                ) : (
                  '確認分配'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
